import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Logo from './icons/Logo';
import LogoSmall from './icons/LogoSmall';
import AccountDropdown from './account-dropdown';
import DropdownMenu from './dropdown-menu';
import { Profile } from '@finn-no/profile-client';
import { Organisation } from '@finn-no/organisation-http-client';

function Menu(props: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <nav
            className="top-bar__nav notranslate"
            aria-label="Hovednavigasjon"
            {...props}
        />
    );
}

function Home({ orgId, onClick }: { orgId: number; onClick: () => void }) {
    const href = `/bedrift${(orgId && `?orgId=${orgId}`) || ''}`;
    const titleClass = orgId
        ? 'top-bar__home__title'
        : 'top-bar__home__title_fallback';

    return (
        <>
            <span
                role="img"
                aria-label="FINN.no logoen viser navnet FINN skrevet med
    hvit skrift i et lyseblått rektangel til høyre for en mørkeblå
    kvartsirkel."
            />
            <a
                className="top-bar__home"
                href={href || '#'}
                aria-label="Forside"
                aria-current="false"
                onClick={onClick}
            >
                <span className="top-bar__home__logo">
                    <Logo />
                </span>
                <span className="top-bar__home__logo-small">
                    <LogoSmall />
                </span>
                <span className={titleClass}>Bedriftssenter</span>
            </a>
        </>
    );
}

Home.defaultProps = {
    orgId: undefined,
};

Home.propTypes = {
    orgId: PropTypes.number,
    onClick: PropTypes.func.isRequired,
};

function EmptyTopBar({ children }: { children?: React.ReactNode }) {
    return (
        <>
            <div className="top-bar">{children}</div>
            <div className="top-bar__spacer" />
        </>
    );
}

EmptyTopBar.propTypes = {
    children: PropTypes.node.isRequired,
};

function TopBarWithNavigation({ children }: { children?: React.ReactNode }) {
    return (
        <EmptyTopBar>
            <Menu>{children}</Menu>
        </EmptyTopBar>
    );
}

TopBarWithNavigation.propTypes = {
    children: PropTypes.node.isRequired,
};

function ResizeAware({ callback }: { callback: () => void }) {
    useEffect(() => {
        window.addEventListener('resize', callback);
        return () => window.removeEventListener('resize', callback);
    }, []);
    return null;
}

ResizeAware.propTypes = {
    callback: PropTypes.func.isRequired,
};

export type TopBarProps = {
    organisations: Organisation[];
    profile?: Profile;
    isCompanyProfileOnJobUnleashEnabled: boolean;
    isCompanyProfileOnMotorUnleashEnabled: boolean;
    hideMenu: boolean;
    accountMenuOpen?: boolean;
    track: (name: string, value?: unknown) => void;
    isMobilityPriceoverviewRedirectUnleashEnabled?: boolean;
};

type TopbarState = {
    isAccountMenuOpen: boolean;
    isHamburgerMenuOpen: boolean;
};

export default class TopBar extends React.Component<TopBarProps, TopbarState> {
    static defaultProps = {
        organisations: [],
        profile: undefined,
        hideMenu: false,
        track: () => undefined,
        accountMenuOpen: false,
        isCompanyProfileOnJobUnleashEnabled: false,
        isCompanyProfileOnMotorUnleashEnabled: false,
        isMobilityPriceoverviewRedirectUnleashEnabled: false,
    } as TopBarProps;
    constructor(props: TopBarProps) {
        super(props);

        this.state = {
            isAccountMenuOpen: !!props.accountMenuOpen,
            isHamburgerMenuOpen: false,
        };

        this.handleAccountMenuToggle = this.handleAccountMenuToggle.bind(this);
        this.handleHamburgerMenuToggle =
            this.handleHamburgerMenuToggle.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    handleAccountMenuToggle(outsideClick?: boolean) {
        const { isAccountMenuOpen, isHamburgerMenuOpen } = this.state;
        const { track } = this.props;

        if (!outsideClick) {
            track('account-menu-click', isHamburgerMenuOpen);
        }

        if (!(isHamburgerMenuOpen && outsideClick)) {
            this.setState({
                isAccountMenuOpen: !isAccountMenuOpen && !outsideClick,
                isHamburgerMenuOpen: false,
            });
        }
    }

    handleHamburgerMenuToggle() {
        const { isHamburgerMenuOpen } = this.state;
        const { track } = this.props;
        track('hamburger-menu-click', isHamburgerMenuOpen);

        this.setState({
            isHamburgerMenuOpen: !isHamburgerMenuOpen,
            isAccountMenuOpen: false,
        });
    }

    handleResize() {
        const { isHamburgerMenuOpen } = this.state;
        if (isHamburgerMenuOpen) {
            this.setState({
                isHamburgerMenuOpen: false,
            });
        }
    }

    render() {
        const {
            profile,
            organisations,
            hideMenu,
            isCompanyProfileOnJobUnleashEnabled,
            isCompanyProfileOnMotorUnleashEnabled,
            track,
            isMobilityPriceoverviewRedirectUnleashEnabled,
        } = this.props;
        const { isAccountMenuOpen, isHamburgerMenuOpen } = this.state;

        const activeOrganisation = organisations.find(
            (account) => account.active,
        );

        const menuItems = hideMenu ? null : (
            <>
                <ResizeAware callback={this.handleResize} />
                <AccountDropdown
                    handleAccountMenuToggle={this.handleAccountMenuToggle}
                    profile={profile}
                    organisations={organisations}
                    activeOrganisation={activeOrganisation}
                    isAccountMenuOpen={isAccountMenuOpen}
                    track={track}
                />
                {activeOrganisation && (
                    <DropdownMenu
                        activeOrganisation={activeOrganisation}
                        track={track}
                        handleHamburgerMenuToggle={
                            this.handleHamburgerMenuToggle
                        }
                        isHamburgerMenuOpen={isHamburgerMenuOpen}
                        isCompanyProfileOnJobUnleashEnabled={
                            isCompanyProfileOnJobUnleashEnabled
                        }
                        isCompanyProfileOnMotorUnleashEnabled={
                            isCompanyProfileOnMotorUnleashEnabled
                        }
                        isMobilityPriceoverviewRedirectUnleashEnabled={
                            isMobilityPriceoverviewRedirectUnleashEnabled
                        }
                    />
                )}
            </>
        );

        return (
            <EmptyTopBar>
                <Menu>
                    <Home
                        orgId={activeOrganisation && activeOrganisation.orgId}
                        onClick={() => track('home-button-click')}
                    />
                    {menuItems}
                </Menu>
            </EmptyTopBar>
        );
    }
}
